/**
 * Live-board score refresh helpers.
 * Primary updates arrive via Socket.IO LIVE_BOARD_UPDATE after Odds Management
 * cron; HTTP get-live-Odds-List is fallback (reconnect, tab focus, long interval).
 */

/** Must match backend LIVE_BOARD_UPDATE_EVENT. */
export const LIVE_BOARD_UPDATE_EVENT = "LIVE_BOARD_UPDATE";

/** Slow HTTP safety net when socket events are missed or disconnected. */
export const LIVE_BOARD_SCORE_FALLBACK_MS = 4 * 60 * 1000;

/** @deprecated Use LIVE_BOARD_SCORE_FALLBACK_MS; kept for tests referencing legacy poll. */
export const LIVE_BOARD_SCORE_REFRESH_MS = LIVE_BOARD_SCORE_FALLBACK_MS;

/**
 * Overlay live-list score fields onto the current board games by id.
 * Keeps board order / non-live games; only patches matching ids.
 */
export function mergeLiveBoardScoreFields(boardGames = [], liveGames = []) {
  const board = Array.isArray(boardGames) ? boardGames : [];
  const live = Array.isArray(liveGames) ? liveGames : [];
  if (!board.length || !live.length) return board;

  const byId = new Map();
  for (const game of live) {
    if (game?.id) byId.set(game.id, game);
  }
  if (!byId.size) return board;

  let changed = false;
  const next = board.map((game) => {
    const liveGame = byId.get(game?.id);
    if (!liveGame) return game;

    const scores =
      liveGame.scores !== undefined ? liveGame.scores : game.scores;
    const completed =
      typeof liveGame.completed === "boolean"
        ? liveGame.completed
        : game.completed;
    const last_update = liveGame.last_update ?? game.last_update;
    const bookmakers =
      Array.isArray(liveGame.bookmakers) && liveGame.bookmakers.length
        ? liveGame.bookmakers
        : game.bookmakers;

    if (
      scores === game.scores &&
      completed === game.completed &&
      last_update === game.last_update &&
      bookmakers === game.bookmakers
    ) {
      return game;
    }

    changed = true;
    return {
      ...game,
      scores,
      completed,
      last_update,
      bookmakers,
    };
  });

  return changed ? next : board;
}

/**
 * Merge score fields and append any new live rows not already on the board
 * (Live tab socket deltas).
 */
export function mergeLiveBoardDelta(boardGames = [], liveGames = []) {
  const board = Array.isArray(boardGames) ? boardGames : [];
  const live = Array.isArray(liveGames) ? liveGames : [];
  if (!live.length) return board;

  const merged = mergeLiveBoardScoreFields(board, live);
  const ids = new Set(merged.map((g) => g?.id).filter(Boolean));
  const extras = live.filter((g) => g?.id && !ids.has(g.id));
  if (!extras.length) return merged;
  return [...merged, ...extras];
}

/** Normalize socket or HTTP payload into a games array. */
export function gamesFromLiveBoardUpdate(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.games)) return payload.games;
  return [];
}

/** Patch a single selected/detail game from a live list row. */
export function mergeLiveFieldsIntoGame(game, liveGames = []) {
  if (!game?.id) return game;
  const live = Array.isArray(liveGames) ? liveGames : [];
  const liveGame = live.find((g) => g?.id === game.id);
  if (!liveGame) return game;
  const merged = mergeLiveBoardScoreFields([game], [liveGame]);
  return merged[0] || game;
}
